import {responseStatus, serverMessage} from "../utils/ErrorUtils";

/**
 * Why an authenticated account API request failed, at the granularity HTTP status can actually decide.
 *
 * Deliberately coarse. Account endpoints express their domain-specific distinction in the localized
 * `message` they already return — and return it in the caller's language because `Accept-Language` rides
 * every request. Re-deriving cases here by matching on message text would restate the api's vocabulary in
 * the client and break the moment a sentence is reworded, so {@link accountApiFailureText} renders the
 * server's sentence and this enum only covers what the ui must behave differently about.
 */
export enum AccountApiFailureKind {
	/** The request never got an answer — there is no server sentence to show. */
	NETWORK = "NETWORK",
	/** The server refused and explained why, in the user's language. */
	REJECTED = "REJECTED",
	/** The server answered but said nothing usable, so the ui supplies the sentence. */
	GENERIC = "GENERIC",
}

export type AccountApiFailureMessageKey = "network" | "generic";

/** The catalog key to use when there is no server sentence to render. */
export function accountApiFailureMessageKey(kind: AccountApiFailureKind): AccountApiFailureMessageKey {
	return kind === AccountApiFailureKind.NETWORK ? "network" : "generic";
}

/** The shape this module needs off a rejected request; anything richer belongs to the caller. */
export interface AccountApiFailureInput {
	/** HTTP status, or null when the request never got an answer. */
	status: number | null;
	/** The api's already-localized `message`, untrusted until narrowed at this boundary. */
	message: unknown;
}

export function classifyAccountApiFailure(
	{status, message}: AccountApiFailureInput,
): AccountApiFailureKind {
	if (status === null) {
		return AccountApiFailureKind.NETWORK;
	}
	return typeof message === "string" && message.trim().length > 0
		? AccountApiFailureKind.REJECTED
		: AccountApiFailureKind.GENERIC;
}

/**
 * The sentence to show for an account API failure: the api's own localized explanation when it gave one,
 * and a catalog fallback otherwise.
 *
 * @param fallbacks the catalog's `network` and `generic` sentences, passed in so this stays React-free
 */
export function accountApiFailureText(
	failure: AccountApiFailureInput,
	fallbacks: Record<AccountApiFailureMessageKey, string>,
): string {
	const kind = classifyAccountApiFailure(failure);
	if (kind === AccountApiFailureKind.REJECTED && typeof failure.message === "string") {
		return failure.message;
	}
	return fallbacks[accountApiFailureMessageKey(kind)];
}

/**
 * The api's own sentence when it REFUSED a request and said why, or null.
 *
 * A 400 is the api's refusal: a bean-validation failure or a business rule, and its `message` is already
 * in the page's language because `Accept-Language` rides every request (ui#125). Every other status is
 * left to the caller's catalog copy, because each already names its own remedy (sign in again, wait,
 * retry) and a 5xx body is the api's sanitized generic sentence. Whether the body "said something usable"
 * is {@link classifyAccountApiFailure}'s question, so the surfaces that show a refusal cannot disagree
 * about an empty body.
 *
 * Only the api's `message` field is ever read. A client-thrown `Error.message` never reaches the page:
 * that was the English leak ui#338 closed.
 */
export function apiRefusalMessageOf(error: unknown): string | null {
	const status = responseStatus(error);
	const message = serverMessage(error);
	return status === 400 && classifyAccountApiFailure({status, message}) === AccountApiFailureKind.REJECTED
		? message
		: null;
}
